<template>
  <div v-if="config.enableAppHeader || isLoggedIn" :class="{ 'onboarding-mode': isOnboardingRoute }">
    <!-- ONBOARDING ROUTES -->
    <template v-if="isOnboardingRoute">
      <div class="app-header-onboarding-spacer" aria-hidden="true"></div>
      <header class="app-header onboarding">
        <div v-if="isViewingAppForm">
          <router-link class="back-to-offers-btn" :to="{ name: 'my-offers' }">
            <i class="material-icons">arrow_back</i>
            <span>Back to <strong>My applications</strong></span>
          </router-link>
        </div>

        <div class="logo-wrapper">
          <SwoopLogo @click="handleLogoRedirect" height="36px" v-if="config.enableAppHeaderLogo" logoType="default" />
          <h1 v-else-if="isExternalGroup && userGroupName !== 'Swoop Funding'">{{ userGroupName }}</h1>
        </div>

        <div class="dropdown-menu" v-if="dropdownEnabled && isLoggedIn">
          <span
            class="need-help"
            :class="{ active: activeIcon === navMenuTypes.CONTACT }"
            :ref="navMenuTypes.CONTACT"
            tabindex="0"
            @click="openNavMenu(navMenuTypes.CONTACT, true)"
            @keydown.enter.prevent="openNavMenu(navMenuTypes.CONTACT, true)"
            @keydown.space.prevent="openNavMenu(navMenuTypes.CONTACT, true)"
          >
            <span class="need-help-text">Need help</span>
            <span class="material-icons-outlined">help_outline</span>
          </span>
          <span
            class="material-icons-outlined menu-item-icon"
            :class="{ active: activeIcon === navMenuTypes.MAIN }"
            :ref="navMenuTypes.MAIN"
            @click="openNavMenu(navMenuTypes.MAIN, true)"
          >
            account_circle
          </span>
        </div>

        <div class="dropdown-menu onboarding-actions" v-if="dropdownEnabled && !isLoggedIn">
          <span
            class="need-help"
            :class="{ active: activeIcon === navMenuTypes.CONTACT }"
            :ref="navMenuTypes.CONTACT"
            tabindex="0"
            @click="openNavMenu(navMenuTypes.CONTACT, true)"
            @keydown.enter.prevent="openNavMenu(navMenuTypes.CONTACT, true)"
            @keydown.space.prevent="openNavMenu(navMenuTypes.CONTACT, true)"
          >
            <span class="need-help-text">Need help</span>
            <span class="material-icons-outlined">help_outline</span>
          </span>
          <button class="sign-in-btn" @click="go({ name: 'integrations' })">Sign in</button>
        </div>
      </header>
    </template>

    <!-- NON-ONBOARDING ROUTES -->
    <template v-else>
      <div class="app-header-mobile-spacer" :class="{ 'with-strip': isLoggedIn }" aria-hidden="true"></div>

      <div v-if="isLoggedIn" class="mobile-top-strip" :class="{ 'is-swoop-brand': isSwoopBranded }">
        <div class="mobile-hamburger-wrapper">
          <span
            class="material-icons mobile-hamburger"
            role="button"
            tabindex="0"
            aria-label="Open menu"
            @click="toggleMobileSidebar"
            @keydown.enter="toggleMobileSidebar"
            @keydown.space.prevent="toggleMobileSidebar"
          >
            menu
          </span>
          <AlertBadge :count="applicationsNeedingAttention" />
        </div>
        <div class="mobile-top-strip-actions">
          <div
            v-if="user"
            class="user-avatar mobile-avatar"
            :class="{ 'is-menu-open': mobileUserMenuOpen }"
            :title="userDisplayName"
            role="button"
            tabindex="0"
            :aria-expanded="mobileUserMenuOpen"
            :aria-label="mobileUserMenuOpen ? 'Close user menu' : 'Open user menu'"
            @click="toggleMobileUserMenu"
            @keydown.enter="toggleMobileUserMenu"
            @keydown.space.prevent="toggleMobileUserMenu"
          >
            {{ userInitials }}
          </div>
        </div>
      </div>

      <template v-if="mobileUserMenuOpen">
        <div class="mobile-user-menu-backdrop" @click="closeMobileUserMenu"></div>
        <div class="mobile-user-menu" :class="{ 'is-swoop-brand': isSwoopBranded }">
          <router-link v-if="!user?.isXeroRestrictedUser" :to="{ name: 'profile' }" class="mobile-user-menu-item" @click.native="closeMobileUserMenu">My profile</router-link>
          <div class="mobile-user-menu-divider"></div>
          <button class="mobile-user-menu-item logout" @click="logout">
            <span class="material-icons-outlined">power_settings_new</span>
            Sign out
          </button>
        </div>
      </template>

      <header class="app-header" :class="[{ 'blur-background': mobileSidebarOpen || rightBar, 'with-strip': isLoggedIn }, isOnboardingRoute || !isLoggedIn ? 'onboarding' : '']">
        <div v-if="isViewingAppForm">
          <router-link class="back-to-offers-btn" :to="{ name: 'my-offers' }">
            <i class="material-icons">arrow_back</i>
            <span>Back to <strong>My applications</strong></span>
          </router-link>
        </div>

        <template v-else>
          <div class="page-title-block" v-if="pageTitle">
            <h1 class="page-title">{{ pageTitle }}</h1>
            <p class="page-subtitle" v-if="pageSubtitle">{{ pageSubtitle }}</p>
          </div>
          <div v-else-if="isExternalGroup && userGroupName !== 'Swoop Funding'" class="page-title">{{ userGroupName }}</div>
        </template>

        <div class="header-actions" v-if="dropdownEnabled && isLoggedIn">
          <button class="need-help-link" :class="{ active: activeIcon === navMenuTypes.CONTACT }" :ref="navMenuTypes.CONTACT" @click="openNavMenu(navMenuTypes.CONTACT, true)">
            Need help?
          </button>
        </div>

        <div class="header-actions" v-if="dropdownEnabled && !isLoggedIn">
          <button class="need-help-link" :class="{ active: activeIcon === navMenuTypes.CONTACT }" :ref="navMenuTypes.CONTACT" @click="openNavMenu(navMenuTypes.CONTACT, true)">
            Need help?
          </button>
          <span
            class="material-icons-outlined menu-item-icon"
            :class="{ active: activeIcon === navMenuTypes.SIGNIN }"
            :ref="navMenuTypes.SIGNIN"
            @click="openNavMenu(navMenuTypes.SIGNIN, true)"
          >
            account_circle
          </span>
        </div>
      </header>
    </template>

    <NavMenu
      :type="navMenuType"
      :navMenuWidth="getNavMenuWidth(navMenuType)"
      :navMenuOpen="navMenuOpen"
      :navMenuPosition="navMenuPosition"
      :user="user"
      :companyName="companyName"
      :companyInitials="companyInitials"
      :isXeroRestrictedUser="user?.isXeroRestrictedUser"
      @navigate="go"
      @logout="logout"
      @close-menu="handleNavMenuStatus(false)"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { navigationBarsGetters, rightHandSideBar } from '@/store/modules/navigationBars/routines'
import { authGetters, logoutRoutine } from '@/store/modules/auth/routines'
import { onboardingRoutes } from '@/router/routerGuards'
import NavMenu from '@/components/common/NavMenu.vue'
import SwoopLogo from '@/components/SwoopLogo.vue'
import AlertBadge from '@/components/AlertBadge.vue'
import { applicationsGetters } from '@/store/modules/applications/routines'
import useWhitelabel from '@/composables/useWhitelabel'
import { navMenuTypes } from '@/utils/constants'
import { isSwoopBrandedTenant } from '@/utils/tenant'

// Wins over the route's meta.sitemap.title, which is the name the chatbot uses and is not always
// what belongs in the header. Routes absent from here fall back to it.
const ROUTE_TITLES = {
  dashboard: 'Dashboard',
  chat: 'Assistant',
  'grant-matches': 'Grant funding',
  'research-and-development': 'R&D funding',
  'credit-cards': 'Credit cards',
  'equity-matches': 'Equity funding',
  'accounting-insights': 'Accounting insights',
  'banking-insights': 'Banking insights',
  'business-insurance': 'Business insurance',
  utilities: 'Energy savings',
  'foreign-exchange': 'Foreign exchange',
  pension: 'Pensions',
  'business-savings-accounts': 'Business savings accounts',
  'business-checking-accounts': 'Business checking accounts',
  'business-credit-cards': 'Business credit cards',
  'credit-health-check': 'Credit affordability',
  'credit-score-improvements': 'Credit score improvements',
  'cashflow-forecast': 'Cash flow & forecasting',
  'cashflow-forecasting-integration': 'Cash flow & forecasting',
  profile: 'Personal info'
}

const ROUTE_SUBTITLES = {
  dashboard: 'See your funding options, discover valuable insights into your business, and find business savings - all from one place.'
}

export default {
  name: 'AppHeader',
  components: { NavMenu, SwoopLogo, AlertBadge },
  props: {
    dropdownEnabled: {
      type: Boolean,
      default: true
    },
    mobileSidebarOpen: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const { config } = useWhitelabel('global')
    return { config }
  },
  data() {
    return {
      navMenuOpen: false,
      isOnboardingRoute: false,
      navMenuWidth: 312,
      navMenuPosition: { x: 0, y: 0 },
      activeIcon: null,
      navMenuType: '',
      mobileUserMenuOpen: false
    }
  },
  computed: {
    ...mapGetters({
      user: authGetters.USER,
      isLoggedIn: authGetters.IS_AUTHENTICATED,
      isExternalGroup: authGetters.IS_EXTERNAL_GROUP,
      userGroupName: authGetters.GROUP_NAME,
      rightBar: navigationBarsGetters.RIGHT_HAND_SIDE_BAR
    }),
    ...mapGetters('applications', {
      applicationsNeedingAttention: applicationsGetters.ACTIONABLE_INFORMATION_REQUESTED_COUNT
    }),
    isViewingAppForm() {
      return this.$route.matched.some(route => route.name === 'application-layout')
    },
    renderVersion() {
      return this.$route?.query.formVersion
    },
    navMenuTypes() {
      return navMenuTypes
    },
    canHeaderBeInteractedWith() {
      // prevent non skippable details form being skipped with navigation
      // return this.$route.name !== 'details-page' // TODO: details-page is currently skippable so removed for now. However, if on the new onboarding we need this, it can be acrivated here.
      return true
    },
    pageTitle() {
      if (this.isOnboardingRoute || !this.isLoggedIn) return ''
      if (this.$route?.name === 'dashboard') {
        const firstName = this.user?.firstName?.trim()
        return firstName ? `Welcome ${firstName}!` : 'Welcome!'
      }
      const metaTitle = this.$route?.meta?.sitemap?.title
      if (metaTitle) return metaTitle
      return ROUTE_TITLES[this.$route?.name] || ''
    },
    pageSubtitle() {
      if (this.isOnboardingRoute || !this.isLoggedIn) return ''
      return ROUTE_SUBTITLES[this.$route?.name] || ''
    },
    companyName() {
      return this.user?.companies?.[this.user.companies.length - 1]?.companyName || ''
    },
    companyInitials() {
      const splittedNames = this.companyName.split(' ') || []
      const initialsList = splittedNames.slice(0, 2).map(name => name.charAt(0).toUpperCase())
      return initialsList.join('')
    },
    userDisplayName() {
      return [this.user?.firstName, this.user?.lastName].filter(Boolean).join(' ') || this.user?.email || ''
    },
    userInitials() {
      const fi = (this.user?.firstName || '').charAt(0).toUpperCase()
      const li = (this.user?.lastName || '').charAt(0).toUpperCase()
      return fi + li || (this.user?.email || '?').charAt(0).toUpperCase()
    },
    isSwoopBranded() {
      return isSwoopBrandedTenant(this.config?.tenantId)
    }
  },
  mounted() {
    window.addEventListener('resize', this.updateNavMenuPosition)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateNavMenuPosition)
  },
  methods: {
    ...mapActions({
      triggerLogout: logoutRoutine.TRIGGER,
      handleRightBarStatus: rightHandSideBar.TRIGGER
    }),
    toggleMobileSidebar() {
      this.closeMobileUserMenu()
      this.$emit('toggle-mobile-sidebar')
    },
    toggleMobileUserMenu() {
      this.mobileUserMenuOpen = !this.mobileUserMenuOpen
    },
    closeMobileUserMenu() {
      this.mobileUserMenuOpen = false
    },
    handleNavMenuStatus(status) {
      if (!this.canHeaderBeInteractedWith) return
      this.navMenuOpen = status
      if (status === false) {
        this.activeIcon = null
        this.navMenuType = ''
      }
      this.handleRightBarStatus(status)
    },
    openNavMenu(icon, status) {
      this.activeIcon = status ? icon : null
      this.navMenuType = icon
      this.updateNavMenuPosition()
      this.handleNavMenuStatus(status)
    },
    updateNavMenuPosition() {
      if (!this.activeIcon) return
      const iconElement = this.$refs[this.activeIcon]
      if (!iconElement) return
      const el = iconElement.$el || iconElement
      const rect = el.getBoundingClientRect()
      this.navMenuPosition = {
        x: rect.right - this.getNavMenuWidth(this.navMenuType),
        y: rect.bottom + 12
      }
    },
    getNavMenuWidth(type) {
      switch (type) {
        case navMenuTypes.SIGNIN:
          return 205
        case navMenuTypes.CONTACT:
          return 240
        case navMenuTypes.MAIN:
          return window.innerWidth < 350 ? 280 : 312
        default:
          return window.innerWidth < 350 ? 280 : 312
      }
    },
    go(to) {
      if (!this.canHeaderBeInteractedWith) return
      this.$router
        .push(to)
        .then(() => this.handleNavMenuStatus(false))
        .catch(() => this.handleNavMenuStatus(false))
    },
    logout() {
      this.closeMobileUserMenu()
      this.$emit('close-mobile-sidebar')
      this.handleRightBarStatus(false)
      this.triggerLogout()
    },
    handleLogoRedirect() {
      if (this.user?.xeroUserType === 'restricted') {
        this.go({ name: 'integrations' })
      } else {
        this.go({ name: 'dashboard' })
      }
    }
  },
  watch: {
    $route: {
      handler: function (route) {
        if (route.matched.length) {
          this.isOnboardingRoute = onboardingRoutes.includes(route.matched[0].name)
        }
      },
      deep: true,
      immediate: true
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/swoop/variables.scss';

// Scoped under .onboarding-mode so these rules can't bleed into the non-onboarding header below.
.onboarding-mode {
  .app-header-onboarding-spacer {
    height: $appbar-height;
    background-color: $color-white;
  }

  header.app-header {
    width: 100%;
    display: flex;
    justify-content: space-between;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    padding: 24px;
    border-bottom: 1px solid $color-gray;
    align-items: center;
    background-color: $color-white;
    z-index: 100;
    height: $appbar-height;

    &.onboarding {
      height: 64px;
      background-color: $color-onboarding-header;
      color: $color-onboarding-header-text;
      border-bottom: 1px solid #dfdfdf;
    }

    .logo-wrapper {
      display: flex;
      margin-left: 10px;
      min-height: 36px;

      ::v-deep .swoop-logo {
        margin: 0;
      }

      &:hover {
        cursor: pointer;
      }

      h1 {
        margin-bottom: 0;
        font-weight: 600;
        color: $color-black;
      }
    }

    .dropdown-menu {
      display: flex;
      align-items: center;
      cursor: pointer;
      gap: 16px;

      @media only screen and (max-width: $breakpoint-sm-max) {
        gap: 8px;
      }

      .menu-item-icon {
        &.active {
          color: var(--color-secondary-500);
        }
      }

      .need-help {
        display: flex;
        align-items: center;
        gap: 2px;
        font-size: 16px;
        line-height: 22px;
        font-weight: 500;
        color: #1d76ac;
        cursor: pointer;

        &:hover {
          color: #2c94cb;

          .need-help-text {
            text-decoration: underline;
          }
        }

        &:active {
          color: #1b6798;

          .need-help-text {
            text-decoration: underline;
          }
        }

        &:focus-visible {
          color: #1b6798;
          outline: 2px solid #1d76ac;
          outline-offset: 3px;
          border-radius: 3px;

          .need-help-text {
            text-decoration: underline;
          }
        }

        .material-icons-outlined {
          font-size: 20px;
          width: 20px;
          color: inherit;
        }
      }

      &.onboarding-actions {
        gap: 24px;

        .sign-in-btn {
          font-size: 16px;
          line-height: 22px;
          font-weight: 700;
          padding: 8px 16px;
          border: 1px solid #bababa;
          border-radius: 8px;
          background: transparent;
          color: var(--color-neutral-500);
          cursor: pointer;
          font-family: inherit;

          &:hover {
            background: var(--color-neutral-50);
          }
        }
      }
    }

    @media only screen and (min-width: $breakpoint-xxs) {
      .logo-wrapper .swoop-logo {
        width: 160px;
      }
    }

    @media only screen and (min-width: $breakpoint-sm) {
      .logo-wrapper .swoop-logo {
        width: 245px;
      }
    }

    @media only screen and (max-width: $breakpoint-sm-max) {
      padding: 24px 12px;

      .logo-wrapper {
        margin: 0 24px 0 0;

        .swoop-logo {
          width: 80px;
          height: auto;
        }
      }
    }

    @media only screen and (max-width: $breakpoint-xs-max) {
      .dropdown-menu .need-help .need-help-text {
        display: none;
      }

      .dropdown-menu .need-help .material-icons-outlined {
        color: #666666;
      }

      .dropdown-menu.onboarding-actions {
        gap: 16px;

        .sign-in-btn {
          border: none;
          padding: 0;
          font-weight: 700;
          color: #1d76ac;

          &:hover {
            background: transparent;
          }
        }
      }
    }
  }
}

.app-header-mobile-spacer {
  display: none;

  @media only screen and (max-width: $breakpoint-sm-max) {
    display: block;
    height: 64px;

    &.with-strip {
      height: 120px;
    }
  }
}

.mobile-top-strip {
  display: none;

  @media only screen and (max-width: $breakpoint-sm-max) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 56px;
    padding: 0 12px;
    background-color: var(--color-primary-500);
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 51;
  }

  // Swoop-region strip bg override to match the desktop sidebar's #012734.
  &.is-swoop-brand {
    @media only screen and (max-width: $breakpoint-sm-max) {
      background-color: #012734;
    }
  }

  .mobile-hamburger-wrapper {
    position: relative;
    display: flex;
    align-items: center;
  }

  .mobile-hamburger {
    color: var(--color-white);
    cursor: pointer;
    padding: 8px;
    font-size: 24px;
    user-select: none;

    &:focus-visible {
      outline: 2px solid var(--color-white);
      outline-offset: 2px;
      border-radius: 4px;
    }
  }

  .alert-badge {
    position: absolute;
    top: 0;
    right: 0;
    min-width: 18px;
    height: 18px;
    border-radius: 9px;
    font-size: 11px;
    line-height: 18px;
    pointer-events: none;
  }

  .mobile-top-strip-actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .mobile-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: var(--color-secondary-500);
    color: var(--color-white);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 13px;
    flex-shrink: 0;
    user-select: none;
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid var(--color-white);
      outline-offset: 2px;
    }
  }
}

.mobile-user-menu-backdrop {
  display: none;

  @media only screen and (max-width: $breakpoint-sm-max) {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 50;
    background-color: transparent;
  }
}

.mobile-user-menu {
  display: none;

  @media only screen and (max-width: $breakpoint-sm-max) {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 56px;
    right: 0;
    z-index: 52;
    background-color: var(--color-primary-500);
    border-radius: 0 0 8px 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
    padding: 8px;
    gap: 2px;
    min-width: 200px;
  }

  &.is-swoop-brand {
    @media only screen and (max-width: $breakpoint-sm-max) {
      background-color: #012734;
    }
  }

  .mobile-user-menu-item {
    background: transparent;
    border: 1px solid transparent;
    color: #bababa;
    font-size: 16px;
    font-weight: 400;
    line-height: 22px;
    letter-spacing: -0.4px;
    text-align: left;
    padding: 8px 12px;
    border-radius: 6px;
    text-decoration: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: inherit;

    &:hover,
    &:focus {
      color: var(--color-white);
      text-decoration: none;
    }

    &.router-link-active,
    &.router-link-active:hover {
      border-color: #a8e7da;
      color: #a8e7da;
    }

    &.logout {
      color: #bababa;

      .material-icons-outlined {
        font-size: 24px;
        color: #fca5a5;
      }

      &:hover {
        color: var(--color-white);
      }
    }
  }

  .mobile-user-menu-divider {
    height: 1px;
    background-color: rgba(255, 255, 255, 0.12);
    margin: 4px 0;
  }
}

header.app-header {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid var(--color-neutral-100, #e9eaea);
  background-color: var(--color-white);
  min-height: 64px;
  box-sizing: border-box;
  position: sticky;
  top: 0;
  z-index: 50;

  @media only screen and (max-width: $breakpoint-sm-max) {
    padding: 12px 16px;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 49;
  }

  // Strip renders above the header → push the header down by the strip's height.
  &.with-strip {
    @media only screen and (max-width: $breakpoint-sm-max) {
      top: 56px;
    }
  }

  &.onboarding {
    height: 64px;
    background-color: $color-onboarding-header;
    color: $color-onboarding-header-text;
    border-bottom: none;
  }

  .page-title {
    font-size: 20px;
    font-weight: 600;
    color: var(--color-neutral-700, #3d3d3d);
    margin: 0;
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    @media only screen and (max-width: $breakpoint-sm-max) {
      font-size: 18px;
    }
  }

  .page-title-block {
    min-width: 0;
  }

  .page-subtitle {
    font-size: 14px;
    font-weight: 400;
    color: var(--color-neutral-600, #5c5f5f);
    margin: 4px 0 0;
    line-height: 1.4;

    // The mobile header is fixed with a fixed-height spacer, so extra lines would overlap the page.
    @media only screen and (max-width: $breakpoint-sm-max) {
      display: none;
    }
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-left: auto;

    .need-help-link {
      background: transparent;
      border: 0;
      color: #1d76ac;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      padding: 6px 8px;
      font-family: inherit;

      &:hover {
        color: #2c94cb;
        text-decoration: underline;
      }

      &:active,
      &.active {
        color: #1b6798;
        text-decoration: underline;
      }

      &:focus-visible {
        color: #1b6798;
        outline: 2px solid #1d76ac;
        outline-offset: 3px;
        border-radius: 3px;
      }
    }

    .menu-item-icon {
      color: var(--color-neutral-900);
      font-size: 28px;
      cursor: pointer;

      &.active {
        color: var(--color-secondary-500);
      }
    }

    p.welcome-company-message {
      display: flex;
      align-items: center;
      margin: 0px;
      font-size: 14px;
    }

    &.onboarding-actions {
      gap: 24px;

      .sign-in-btn {
        font-size: 16px;
        line-height: 22px;
        font-weight: 700;
        padding: 8px 16px;
        border: 1px solid #bababa;
        border-radius: 8px;
        background: transparent;
        color: var(--color-neutral-500);
        cursor: pointer;

        &:hover {
          background: var(--color-neutral-50);
        }
      }
    }
  }

  .back-to-offers-btn {
    color: #000000;
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 16px;
    border: 0;
    background: transparent;
    cursor: pointer;
    text-decoration: none;

    &:hover,
    &:focus {
      font-weight: inherit;
      text-decoration: none;
    }

    strong {
      font-weight: 700;
    }

    i {
      font-size: 14px;
    }

    @media only screen and (max-width: $breakpoint-sm-max) {
      color: #3d3d3d;
      font-size: 14px;
      font-weight: 400;
      line-height: 18px;
      letter-spacing: -0.4px;

      strong {
        font-weight: 700;
      }
    }
  }
}
</style>
